import { Parser } from '~/api';
import { ServerTransfer } from '~/api/models';
import { Server } from '~/api/models/Server';
import RequestService from './request';

interface CreateServerRequest {
    name: string;
    description?: string;
    owner_id: number;
    egg_id: number;
    docker_image: string;
    startup?: string;
    environment: Record<string, any>;
    skip_scripts?: boolean;
    start_on_completion?: boolean;

    cpu: number;
    cpu_burst: number | null;
    cpu_priority: number | null;
    cpu_period: number | null;
    memory: number;
    swap: number;
    disk: number;
    io: number;

    databases_limit: number;
    allocations_limit: number;
    backup_megabytes_limit: number;
    backup_count_limit: number | null;

    server_template_id?: number | string | null;

    primary_allocation_id: number;
    secondary_allocations_ids: number[];

    // Egg-driven automatic port allocation: a chosen node + IP the egg's ports are
    // drawn from, instead of a specific primary allocation.
    node_id?: number | string | null;
    allocation_ip?: string | null;

    // Automatic deployment: the panel picks a viable node + allocation matching the
    // chosen locations (by id) and tags (by slug), instead of a specific node.
    deploy?: {
        locations?: (number | string)[];
        tags?: string[];
        dedicated_ip?: boolean;
        port_range?: string[];
    };
}

interface DeleteServerRequest {
    force: boolean;
}

interface CloneServerRequest {
    node_id: number;
    primary_allocation_id: number;
    secondary_allocations_ids?: number[];
    reinstall_server: boolean;
    respect_wispignore: boolean;
}

interface UpdateDetailsRequest {
    name: string;
    external_id?: string;
    description: string;
    owner_id: number;
}

interface UpdateBuildRequest {
    cpu: number;
    cpu_burst: number | null;
    cpu_priority: number | null;
    cpu_period: number | null;
    memory: number;
    swap: number;
    disk: number;
    io: number;
    databases_limit: number;
    allocations_limit: number;
    backup_megabytes_limit: number;
    backup_count_limit: number | null;
    allocation_id: number;
    add_allocation_ids: number[];
    remove_allocation_ids: number[];
}

interface SuspendServerRequest {
    suspended: boolean;
}

interface TransferServerRequest {
    node_id: number;
    allocation_id: number;
    allocation_additional?: number[];
    scheduled?: boolean;
}

interface ReinstallServerData {
    wipe_all_files: boolean;
    backup_before_reinstall: boolean;
    start_on_completion: boolean;
}

export interface TemplateDifference {
    kind: 'limit' | 'variable' | 'schedule';
    field: string;
    server: string | null;
    template: string | null;
}

export interface TemplateDivergence {
    type: 'soft' | 'hard';
    differences: TemplateDifference[];
}

class ServersService {
    getAll(req: PaginatableRequest, extraData: PaginatableRequest = {}): Promise<ListResponse> {
        return RequestService.get('/servers', {
            include: ['node', 'allocations', 'user', 'egg', 'serverTemplate'],
            ...req,
            ...extraData,
            sort: req.sort ?? extraData.sort ?? 'position',
            filter: {
                ...(req.filter ?? {}),
                ...(extraData.filter ?? {}),
            },
        }).then(Parser.parse);
    }

    get(): Promise<Server> {
        return RequestService.get('/servers/:server', {
            include: [
                'node',
                'egg',
                'originalEgg',
                'allocations.firewallRules',
                'user',
                'features',
                'serverTemplate',
                'transfer.oldNode',
                'transfer.newNode',
                'transfer.newAllocations',
            ],
        }).then(Parser.parse);
    }

    favorite(server: Server): Promise<void> {
        return RequestService.post(`/servers/${server.getRouteID()}/favorite`);
    }

    unfavorite(server: Server): Promise<void> {
        return RequestService.delete(`/servers/${server.getRouteID()}/favorite`);
    }

    create(data: CreateServerRequest): Promise<Server> {
        return RequestService.post('/servers?include=node', data).then(Parser.parse);
    }

    delete(data: DeleteServerRequest): Promise<void> {
        return RequestService.delete('/servers/:server', { data });
    }

    clone(data: CloneServerRequest): Promise<Server> {
        return RequestService.post('/servers/:server/clone?include=node', data).then(Parser.parse);
    }

    updateDetails(data: UpdateDetailsRequest): Promise<Server> {
        return RequestService.put('/servers/:server/details', data).then(Parser.parse).then(RequestService.updateModelBinding);
    }

    updateBuild(data: UpdateBuildRequest): Promise<Server> {
        return RequestService.put('/servers/:server/build?include=allocations', data).then(Parser.parse).then(RequestService.updateModelBinding);
    }

    attachTemplate(data: { template_id: string }): Promise<Server> {
        return RequestService.post('/servers/:server/template?include=serverTemplate', data)
            .then(Parser.parse)
            .then(RequestService.updateModelBinding);
    }

    detachTemplate(): Promise<Server> {
        return RequestService.delete('/servers/:server/template').then(Parser.parse).then(RequestService.updateModelBinding);
    }

    resyncTemplate(server: Server): Promise<{ dispatched: number }> {
        return RequestService.post(`/servers/${server.getRouteID()}/template/resync`).then((res) => res.data);
    }

    templateDivergence(server: Server): Promise<TemplateDivergence> {
        return RequestService.get(`/servers/${server.getRouteID()}/template/divergence`).then((res) => res.data);
    }

    reinstall(data: ReinstallServerData): Promise<Server> {
        return RequestService.post('/servers/:server/reinstall', data).then(Parser.parse).then(RequestService.updateModelBinding);
    }

    toggleInstall(): Promise<Server> {
        return RequestService.post('/servers/:server/toggle-install').then(Parser.parse).then(RequestService.updateModelBinding);
    }

    rebuild(): Promise<Server> {
        return RequestService.post('/servers/:server/rebuild').then(Parser.parse).then(RequestService.updateModelBinding);
    }

    rebuildEgg(): Promise<void> {
        return RequestService.post('/eggs/:egg/rebuild');
    }

    suspend(data: SuspendServerRequest): Promise<Server> {
        return RequestService.post('/servers/:server/suspension', data).then(Parser.parse).then(RequestService.updateModelBinding);
    }

    toggleUpdate(): Promise<Server> {
        return RequestService.post('/servers/:server/toggle-update').then(Parser.parse).then(RequestService.updateModelBinding);
    }

    toggleMove(): Promise<Server> {
        return RequestService.post('/servers/:server/toggle-move').then(Parser.parse).then(RequestService.updateModelBinding);
    }

    resetMods(): Promise<Server> {
        return RequestService.post('/servers/:server/reset-mods').then(Parser.parse).then(RequestService.updateModelBinding);
    }

    transfer(data: TransferServerRequest): Promise<ServerTransfer> {
        return RequestService.post('/servers/:server/transfer', data).then(Parser.parse);
    }

    cancelScheduledTransfer(): Promise<Server> {
        return RequestService.delete('/servers/:server/transfer/scheduled').then(Parser.parse).then(RequestService.updateModelBinding);
    }

    createFirewallRules(): Promise<Server> {
        return RequestService.post('/servers/:server/firewall/create').then(Parser.parse).then(RequestService.updateModelBinding);
    }

    deleteFirewallRules(): Promise<Server> {
        return RequestService.post('/servers/:server/firewall/delete').then(Parser.parse).then(RequestService.updateModelBinding);
    }

    createFirewallRule(data: Record<string, unknown>): Promise<Server> {
        return RequestService.post('/servers/:server/firewall/rules', data).then(Parser.parse).then(RequestService.updateModelBinding);
    }

    deleteFirewallRule(ruleId: number): Promise<void> {
        return RequestService.delete(`/servers/:server/firewall/rules/${ruleId}`);
    }
}

export default new ServersService();
